import {
  filterAssetsByLocale,
  getAssetByTags,
  getAssetsByTags,
  getAttribute,
  getProductAssets,
  getVariantFinalPrice,
  isVariantActive,
  sortAndDedupeFacets,
  sortAssetsByPriority,
} from '@src/utils/product.util'

/** Creates a view model for a digital product */
export function createDigitalViewModel(args: {
  productData: Commercetools.ProductData
  builderContent: any
  locale: string
}): DigitalViewModel {
  filterAssetsByLocale(args.productData, args.locale)
  const productAssets = sortAssetsByPriority(getProductAssets(args.productData))
  const variants = [args.productData.masterVariant]
    .concat(args.productData.variants)
    .map(createDigitalVariant)
    .filter(isVariantActive)
  const masterVariant = variants[0]

  return {
    product: {
      ...args.productData,
      productType: args.productData.productType?.obj?.key ?? '',
      productSubtype: getAttribute(masterVariant, 'productSubtype'),
      productShortDescription: getAttribute(masterVariant, 'productShortDescription'),
      productSeoDescription: getAttribute(masterVariant, 'productSeoDescription'),
      assets: productAssets,
    },
    variants,
    masterVariant,
    selectedVariant: masterVariant,
    accolades: args?.builderContent?.data?.accolades ?? [],
    socialProof: args?.builderContent?.data?.socialProof ?? [],
    alternateProducts: args?.builderContent?.data?.alternateProducts ?? [],
    facetOptions: getDigitalFacetOptions(variants),
    quantity: 1,
  }
}

/** Creates a digital variant from a product variant */
function createDigitalVariant(variant: Commercetools.ProductVariant): DigitalVariant {
  return {
    ...variant,
    assets: sortAssetsByPriority(getAssetsByTags(variant, ['type:sku'])),
    originalPrice: variant.prices?.[0]?.value?.centAmount ?? 0,
    finalPrice: getVariantFinalPrice({ variant }),
    facets: {
      subscriptionFrequency: {
        slug: getAttribute(variant, 'subscriptionFrequencySlug'),
        displayName: getAttribute(variant, 'subscriptionFrequencyDisplayName'),
        marketingName: getAttribute(variant, 'subscriptionFrequencyMarketingName'),
        description: getAttribute(variant, 'subscriptionFrequencyDescription'),
        priority: getAttribute(variant, 'subscriptionFrequencyPriority'),
        priceSuffix: getAttribute(variant, 'subscriptionFrequencyPriceSuffix'),
        bannerText: getAttribute(variant, 'subscriptionFrequencyBannerText'),
        cadence: getAttribute(variant, 'subscriptionFrequencyCadence'),
        imagePrimary: getAssetByTags(variant, [
          'type:facet',
          `subscriptionFrequency:${getAttribute(variant, 'subscriptionFrequencySlug')}:primary`,
        ]),
        imageSecondary: getAssetByTags(variant, [
          'type:facet',
          `subscriptionFrequency:${getAttribute(variant, 'subscriptionFrequencySlug')}:secondary`,
        ]),
      },
    },
  }
}

/** Creates facet options for the digital product */
function getDigitalFacetOptions(variants: DigitalVariant[]): DigitalFacetOptions {
  return {
    subscriptionFrequency: sortAndDedupeFacets({ variants, facetKey: 'subscriptionFrequency' }),
  }
}
